import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import cartService from '../services/cartService';
import { buildImageUrl, sanitizeImageUrl } from '../utils/image';
import { useAuth } from './AuthContext';
import { useI18n } from './I18nContext';

// Initial state
const initialState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  originalPrice: 0,
  totalDiscount: 0,
  appliedPromotions: [],
  isLoading: false,
  error: null,
};

// Action types
const CART_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CART: 'SET_CART',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  CLEAR_CART: 'CLEAR_CART',
  APPLY_PROMOTION: 'APPLY_PROMOTION',
  REMOVE_PROMOTION: 'REMOVE_PROMOTION',
};

// Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case CART_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case CART_ACTIONS.SET_CART:
      // Avoid unnecessary state changes if payload doesn't actually modify cart.
      // This helps prevent effect loops (e.g., recalculation effects depending on callbacks/state).
      if (
        action.payload?.items === state.items &&
        action.payload?.appliedPromotions === state.appliedPromotions &&
        action.payload?.totalPrice === state.totalPrice &&
        action.payload?.originalPrice === state.originalPrice &&
        action.payload?.totalDiscount === state.totalDiscount &&
        action.payload?.totalItems === state.totalItems
      ) {
        return {
          ...state,
          isLoading: false,
          error: null,
        };
      }

      return {
        ...state,
        ...action.payload,
        isLoading: false,
        error: null,
      };

    case CART_ACTIONS.ADD_ITEM: {
      const existingItemIndex = state.items.findIndex(
        (item) =>
          item.productId === action.payload.productId &&
          item.variantId === action.payload.variantId &&
          item.type === action.payload.type
      );

      let updatedItems;
      if (existingItemIndex >= 0) {
        updatedItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        );
      } else {
        updatedItems = [...state.items, action.payload];
      }

      return {
        ...state,
        items: updatedItems,
      };
    }

    case CART_ACTIONS.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map((item) =>
          item.productId === action.payload.productId
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };

    case CART_ACTIONS.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter((item) => item.productId !== action.payload),
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...initialState,
      };

    case CART_ACTIONS.APPLY_PROMOTION:
      return {
        ...state,
        appliedPromotions: [...state.appliedPromotions, action.payload],
      };

    case CART_ACTIONS.REMOVE_PROMOTION:
      return {
        ...state,
        appliedPromotions: state.appliedPromotions.filter(
          (promo) => promo.code !== action.payload
        ),
      };

    default:
      return state;
  }
};

// Create context
const CartContext = createContext();

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// CartProvider component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user } = useAuth();
  const { locale } = useI18n();
  // Cache for price-check responses to avoid repeated network calls
  const priceCheckCacheRef = useRef(new Map());

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        dispatch({ type: CART_ACTIONS.SET_CART, payload: cartData });
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error);
        localStorage.removeItem('cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (state.items.length > 0) {
      localStorage.setItem(
        'cart',
        JSON.stringify({
          items: state.items,
          appliedPromotions: state.appliedPromotions,
        })
      );
    } else {
      localStorage.removeItem('cart');
    }
  }, [state.items, state.appliedPromotions]);

  // Calculate cart totals with pricing
  const calculateCartTotals = useCallback(
    async (itemsOverride) => {
      try {
        dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

        const items = itemsOverride || state.items;

        const cartItems = items.map((item) => ({
          product: { _id: item.productId },
          quantity: item.quantity,
        }));

        const promotionCodes = state.appliedPromotions.map(
          (promo) => promo.code
        );

        const pricing = await cartService.calculateCartPrice({
          items: cartItems,
          promotionCodes,
        });

        dispatch({
          type: CART_ACTIONS.SET_CART,
          payload: {
            totalItems: items.reduce((sum, item) => sum + item.quantity, 0),
            totalPrice: pricing.totalFinalPrice,
            originalPrice: pricing.totalOriginalPrice,
            totalDiscount: pricing.totalDiscount,
            appliedPromotions:
              pricing.appliedPromotions || state.appliedPromotions,
          },
        });
      } catch (error) {
        console.error('Failed to calculate cart totals:', error);
        dispatch({
          type: CART_ACTIONS.SET_ERROR,
          payload: 'Failed to calculate cart totals',
        });
      }
    },
    [state.items, state.appliedPromotions]
  );

  // Recalculate cart totals whenever items change
  useEffect(() => {
    if (state.items.length > 0) {
      calculateCartTotals(state.items);
    }
  }, [state.items, user, calculateCartTotals]);

  // Add item to cart (supports variants and bundles)
  const addToCart = useCallback(
    async (product, quantity = 1, options = {}) => {
      try {
        dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

        // Determine item type
        const itemType = options.type || 'product';
        const variantId = options.variantId || null;
        const bundleId = itemType === 'bundle' ? product.id : null;

        // Check if product/variant can be ordered
        const cacheKey = `${product._id || product.id || product.sku || 'generic'}:${variantId || 'none'}:${quantity}`;
        let priceCheck = priceCheckCacheRef.current.get(cacheKey);
        if (!priceCheck && itemType !== 'bundle') {
          priceCheck = await cartService.checkPrice({
            productId: product._id || product.id,
            variantId,
            sku: product.sku,
            quantity,
          });
          priceCheckCacheRef.current.set(cacheKey, priceCheck);
        }

        // Allow out-of-stock items for furniture (made-to-order)
        if (priceCheck && !priceCheck.canOrder) {
          console.warn(
            `[Cart] Product ${product._id || product.id} is out of stock but will be added (made-to-order):`,
            priceCheck.reason || 'No reason provided'
          );
        }

        const imageKey = product.imageKey || product.images?.primaryKey;
        const sanitizedPrimaryImage =
          sanitizeImageUrl(product.images?.[0]?.url) ||
          sanitizeImageUrl(product.image) ||
          sanitizeImageUrl(product.imageUrl);

        const cartItem = {
          productId: product._id || product.id,
          productName:
            typeof product.name === 'object'
              ? product.name[locale] ||
                product.name.ru ||
                product.name.en ||
                product.name.ky
              : product.name,
          productSlug: product.slug || product.sku || product.id,
          productImage: imageKey
            ? buildImageUrl(imageKey)
            : sanitizedPrimaryImage || '/images/placeholder-product.svg',
          quantity,
          unitPrice:
            options.price ||
            (priceCheck?.pricing?.finalPrice ??
              (product.b2bPrice || product.price)),
          sku: product.sku,
          type: itemType,
          variantId,
          variantName: options.variantName,
          bundleId,
          bundleItems: options.bundleItems,
          options,
        };

        dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: cartItem });

        const displayName =
          typeof product.name === 'object'
            ? product.name[locale] ||
              product.name.ru ||
              product.name.en ||
              product.name.ky
            : product.name;
        const variantText = options.variantName
          ? ` (${options.variantName})`
          : '';
        toast.success(`${displayName}${variantText} добавлен в корзину`);

        return { success: true };
      } catch (error) {
        dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
        toast.error(error.message || 'Не удалось добавить товар в корзину');
        throw error;
      }
    },
    [locale]
  );

  // Remove item from cart (defined before updateQuantity due to dependency)
  const removeFromCart = useCallback((productId) => {
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: productId });
    toast.info('Товар удален из корзины');
  }, []);

  // Update item quantity
  const updateQuantity = useCallback(
    async (productId, quantity) => {
      if (quantity <= 0) {
        return removeFromCart(productId);
      }

      try {
        dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

        // Check if new quantity can be ordered
        const cacheKey = `${productId}:${quantity}`;
        let priceCheck = priceCheckCacheRef.current.get(cacheKey);
        if (!priceCheck) {
          priceCheck = await cartService.checkPrice({
            productId,
            quantity,
          });
          priceCheckCacheRef.current.set(cacheKey, priceCheck);
        }

        if (!priceCheck.canOrder) {
          throw new Error(
            priceCheck.reason || 'Requested quantity not available'
          );
        }

        dispatch({
          type: CART_ACTIONS.UPDATE_ITEM,
          payload: { productId, quantity },
        });

        return { success: true };
      } catch (error) {
        dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
        toast.error(error.message || 'Не удалось обновить количество');
        throw error;
      }
    },
    [removeFromCart]
  );

  // Clear entire cart
  const clearCart = useCallback(() => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
    localStorage.removeItem('cart');
    toast.info('Корзина очищена');
  }, []);

  // Apply promotion code
  const applyPromotionCode = useCallback(
    async (code) => {
      try {
        dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

        // Check if promotion is already applied
        const isAlreadyApplied = state.appliedPromotions.some(
          (promo) => promo.code === code.toUpperCase()
        );

        if (isAlreadyApplied) {
          throw new Error('Промокод уже применен');
        }

        const cartItems = state.items.map((item) => ({
          product: { _id: item.productId },
          quantity: item.quantity,
        }));

        const result = await cartService.validatePromotionCode({
          code,
          items: cartItems,
        });

        if (result.valid) {
          dispatch({
            type: CART_ACTIONS.APPLY_PROMOTION,
            payload: {
              code: code.toUpperCase(),
              name: result.promotion.name,
              discount: result.discount,
            },
          });

          toast.success(`Промокод "${code}" применен!`);
          return { success: true, discount: result.discount };
        } else {
          throw new Error(result.reason || 'Промокод недействителен');
        }
      } catch (error) {
        dispatch({ type: CART_ACTIONS.SET_ERROR, payload: error.message });
        toast.error(error.message || 'Не удалось применить промокод');
        throw error;
      }
    },
    [state.appliedPromotions, state.items]
  );

  // Remove promotion code
  const removePromotionCode = useCallback((code) => {
    dispatch({ type: CART_ACTIONS.REMOVE_PROMOTION, payload: code });
    toast.info(`Промокод "${code}" удален`);
  }, []);

  // Helper functions - memoized
  const getItemCount = useCallback(() => {
    return state.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [state.items]);

  // Check if product is in cart
  const isInCart = useCallback(
    (productId) => {
      return state.items.some((item) => item.productId === productId);
    },
    [state.items]
  );

  // Get item quantity in cart
  const getItemQuantity = useCallback(
    (productId) => {
      const item = state.items.find((item) => item.productId === productId);
      return item ? item.quantity : 0;
    },
    [state.items]
  );

  // Get cart summary for B2B quick order
  const getCartSummary = useCallback(() => {
    return {
      totalItems: state.items.reduce((sum, item) => sum + item.quantity, 0),
      totalPrice: state.totalPrice,
      originalPrice: state.originalPrice,
      totalDiscount: state.totalDiscount,
      itemCount: state.items.length,
      hasB2BPricing: user && user.role === 'b2b_verified',
    };
  }, [
    state.items,
    state.totalPrice,
    state.originalPrice,
    state.totalDiscount,
    user,
  ]);

  // Context value - MEMOIZED to prevent re-renders
  const value = useMemo(
    () => ({
      // State
      items: state.items,
      totalItems: state.items.reduce((sum, item) => sum + item.quantity, 0),
      totalPrice: state.totalPrice,
      originalPrice: state.originalPrice,
      totalDiscount: state.totalDiscount,
      appliedPromotions: state.appliedPromotions,
      isLoading: state.isLoading,
      error: state.error,

      // Actions
      addToCart,
      addItem: addToCart, // Alias for compatibility
      updateQuantity,
      removeFromCart,
      clearCart,
      applyPromotionCode,
      removePromotionCode,
      calculateCartTotals,

      // Helper functions
      getItemCount,
      isInCart,
      getItemQuantity,
      getCartSummary,
    }),
    [
      state.items,
      state.totalPrice,
      state.originalPrice,
      state.totalDiscount,
      state.appliedPromotions,
      state.isLoading,
      state.error,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      applyPromotionCode,
      removePromotionCode,
      calculateCartTotals,
      getItemCount,
      isInCart,
      getItemQuantity,
      getCartSummary,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
